"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDown } from "lucide-react";
import { useCallback, useId } from "react";
import { statusIcons } from "@/components/status";
import { Button } from "@defuse/ui/button";
import { Checkbox } from "@defuse/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@defuse/ui/dropdown-menu";
import { Label } from "@defuse/ui/label";
import { type StatusTypeVisibleToUser, defaultStatuses, displayStatus, statusesVisibleToUser, statusesVisibleToUserArray } from "@intents-explorer/shared-api/status";
import { numToStr } from "@/lib/type-safe-stringify";
import { cn } from "@/lib/utils";
function getStatusDisplayName(selectedStatuses: StatusTypeVisibleToUser[] | undefined) {
  if (selectedStatuses === undefined) {
    return `${numToStr(defaultStatuses.length)} Statuses`;
  }
  if (selectedStatuses.length === statusesVisibleToUserArray.length) {
    return "All Statuses";
  }
  if (selectedStatuses.length === 0) {
    return "No statuses";
  }
  const [firstStatus, ...restStatuses] = selectedStatuses;
  if (firstStatus !== undefined && restStatuses.length === 0) {
    return displayStatus(firstStatus);
  }
  return `${numToStr(selectedStatuses.length)} Statuses`;
}
interface StatusFilterProps {
  statuses: StatusTypeVisibleToUser[] | undefined;
  updateFilters: (filters: {
    status: StatusTypeVisibleToUser[] | undefined;
  }) => void;
}
export function StatusFilter(t0) {
  const $ = _c(42);
  const {
    statuses,
    updateFilters
  } = t0;
  const dropdownId = useId();
  const allStatusesId = useId();
  let t1;
  if ($[0] !== statuses) {
    t1 = JSON.stringify(statuses);
    $[0] = statuses;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = JSON.stringify(statusesVisibleToUserArray);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isAllSelected = t1 === t2;
  let t3;
  if ($[3] !== statuses || $[4] !== updateFilters) {
    t3 = (statusKey, checked) => {
      if (checked) {
        const updatedStatuses = [...(statuses ?? defaultStatuses), statusKey].sort();
        if (JSON.stringify(updatedStatuses) === JSON.stringify(defaultStatuses)) {
          updateFilters({
            status: undefined
          });
        } else {
          updateFilters({
            status: updatedStatuses
          });
        }
      } else {
        const updatedStatuses_0 = (statuses ?? defaultStatuses).filter(s => s !== statusKey);
        if (JSON.stringify(updatedStatuses_0) === JSON.stringify(defaultStatuses)) {
          updateFilters({
            status: undefined
          });
        } else {
          updateFilters({
            status: updatedStatuses_0
          });
        }
      }
    };
    $[3] = statuses;
    $[4] = updateFilters;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleStatusToggle = t3;
  let t4;
  if ($[6] !== updateFilters) {
    t4 = checked_0 => {
      const newSelectedStatuses = checked_0 ? statusesVisibleToUserArray : [];
      updateFilters({
        status: newSelectedStatuses
      });
    };
    $[6] = updateFilters;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleAllStatusesToggle = t4;
  let t5;
  if ($[8] !== dropdownId) {
    t5 = <Label htmlFor={dropdownId} className="text-muted-foreground text-xs">Status</Label>;
    $[8] = dropdownId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== statuses) {
    t6 = getStatusDisplayName(statuses);
    $[10] = statuses;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronDown className="ml-1 h-4 w-4" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== dropdownId || $[14] !== t6) {
    t8 = <DropdownMenuTrigger asChild={true}><Button id={dropdownId} className="min-w-44" variant="outline" size="sm">{t6}{t7}</Button></DropdownMenuTrigger>;
    $[13] = dropdownId;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== handleAllStatusesToggle || $[17] !== isAllSelected) {
    t9 = e => {
      e.preventDefault();
      handleAllStatusesToggle(!isAllSelected);
    };
    $[16] = handleAllStatusesToggle;
    $[17] = isAllSelected;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== allStatusesId || $[20] !== isAllSelected) {
    t10 = <Checkbox id={allStatusesId} checked={isAllSelected} />;
    $[19] = allStatusesId;
    $[20] = isAllSelected;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== allStatusesId) {
    t11 = <label htmlFor={allStatusesId} className="text-sm leading-none font-medium">All Statuses</label>;
    $[22] = allStatusesId;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t9) {
    t12 = <DropdownMenuItem className="flex items-center gap-2" onClick={t9}>{t10}{t11}</DropdownMenuItem>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="bg-border h-px" />;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== handleStatusToggle || $[30] !== isAllSelected || $[31] !== statuses) {
    t14 = statusesVisibleToUserArray.map(status => {
      const displayName = statusesVisibleToUser[status];
      const Icon = statusIcons[status];
      const isChecked = isAllSelected || (statuses ?? defaultStatuses).includes(status);
      return <DropdownMenuItem key={status} className="flex items-center gap-2" onClick={e_0 => {
        e_0.preventDefault();
        handleStatusToggle(status, !isChecked);
      }}><Checkbox id={`status-${status}`} checked={isChecked} /><label htmlFor={`status-${status}`} className="flex items-center gap-2 text-sm leading-none font-medium"><Icon size={14} className={cn(status === "PROCESSING" && "animate-spin", "text-muted-foreground")} /><span>{displayName}</span></label></DropdownMenuItem>;
    });
    $[29] = handleStatusToggle;
    $[30] = isAllSelected;
    $[31] = statuses;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t12 || $[34] !== t14) {
    t15 = <DropdownMenuContent align="start" className="flex flex-col gap-2 p-2">{t12}{t13}{t14}</DropdownMenuContent>;
    $[33] = t12;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== t8) {
    t16 = <DropdownMenu>{t8}{t15}</DropdownMenu>;
    $[36] = t15;
    $[37] = t8;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t16 || $[40] !== t5) {
    t17 = <div className="flex flex-col justify-center gap-1">{t5}{t16}</div>;
    $[39] = t16;
    $[40] = t5;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}